import Icons from '../../Icons'
import { lightTheme } from '@/customTheme'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { KIND } from 'baseui/button'
import { useTranslation } from 'react-i18next'
import { NavbarButtonBaseProps } from '../navbarConfig'
import { FloatingInspectorButtonSet } from '@/constants/app-options'

interface NavbarBackButtonProps extends NavbarButtonBaseProps {
  onClick?: () => void
  /**
   * Drives the back-button label for the few flows that use the navbar's
   * `customButtonSet` mode. Defaults to "AI models" so existing call sites
   * keep their previous behavior.
   */
  mode?: FloatingInspectorButtonSet
  /** Explicit label override; takes precedence over `mode`. */
  label?: string
  /** `primary-black`: filled pill, for when back is the screen's main exit CTA. */
  variant?: 'tertiary' | 'primary-black'
  /** Keeps the full accessible label while presenting the control as icon-only. */
  hideLabel?: boolean
}

function NavbarBackButton({
  disabled,
  onClick,
  mode,
  label: labelOverride,
  variant = 'tertiary',
  hideLabel = false,
}: NavbarBackButtonProps) {
  const { t } = useTranslation()
  const isPrimary = variant === 'primary-black'

  const iconAndTextColor = disabled
    ? lightTheme.colors.grayScale200
    : isPrimary
    ? lightTheme.colors.white
    : lightTheme.colors.blackGray
  const label = labelOverride ?? (mode === 'photoshoot' ? t('Photoshoot') : t('AI models'))

  return (
    <ButtonCustom
      type={SizeButton.STANDARD}
      kind={KIND.tertiary}
      disabled={disabled}
      tooltip={hideLabel ? { content: label } : undefined}
      style={{
        width: hideLabel ? '40px' : 'fit-content',
        height: hideLabel ? '40px' : undefined,
        gap: hideLabel ? 0 : isPrimary ? '6px' : '4px',
        padding: hideLabel ? 0 : isPrimary ? '8px 16px 8px 8px' : '8px 12px',
        border: 'none',
        borderRadius: '360px',
        background: isPrimary ? lightTheme.colors.blackGray : 'transparent',
        ':hover': {
          border: 'none',
          background: disabled
            ? 'transparent'
            : isPrimary
            ? lightTheme.colors.black
            : lightTheme.colors.grayScale50,
        },
        ':active': {
          border: 'none',
          background: disabled
            ? 'transparent'
            : isPrimary
            ? lightTheme.colors.blackGray
            : lightTheme.colors.grayScale100,
          opacity: isPrimary ? 0.76 : undefined,
        },
      }}
      onClick={onClick}
    >
      <Icons.Back size={isPrimary ? 24 : 20} fill={iconAndTextColor} />
      <p
        style={{
          ...lightTheme.typography.Small14Semibold,
          color: iconAndTextColor,
          margin: 0,
          whiteSpace: 'nowrap',
          ...(hideLabel
            ? {
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                border: 0,
              }
            : {}),
        }}
      >
        {label}
      </p>
    </ButtonCustom>
  )
}

export default NavbarBackButton
